import {isIosDevice} from './gpuCapabilities'

/**
 * Trigger a file download from the browser.
 *
 * iOS WebKit (Safari + Chrome) ignores the `<a download>` attribute on both
 * `data:` and `blob:` URLs — clicking the synthetic anchor either navigates
 * the tab away or does nothing at all, so the user sees a "render charged"
 * receipt but never gets a file. On iOS we therefore prefer the Web Share API
 * file path (iOS 15+, surfaces the system share sheet with a "Save to Files" /
 * "Save Image" option), and fall back to opening the blob in a new tab so the
 * user can long-press → Save when the share sheet isn't available.
 *
 * Desktop + Android Chrome use the standard `<a download>` flow.
 */
export const downloadFile = async (blob: Blob, fileName: string): Promise<void> => {
  if (isIosDevice()) {
    try {
      const file = new File([blob], fileName, {type: blob.type || 'application/octet-stream'})
      const nav = navigator as Navigator & {
        canShare?: (data: {files: File[]}) => boolean
        share?: (data: {files: File[]; title?: string}) => Promise<void>
      }
      if (nav.canShare && nav.share && nav.canShare({files: [file]})) {
        await nav.share({files: [file], title: fileName})
        return
      }
    } catch (err) {
      // User-cancel of the share sheet is a normal outcome — don't fall through.
      if ((err as {name?: string})?.name === 'AbortError') return
      // Any other failure → fall through to the new-tab fallback below.
    }
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    // The new tab still needs the URL alive; revoke later instead of immediately.
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
    return
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.download = fileName
  link.href = url
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Convert a data URL (e.g. `canvas.toDataURL('image/png')`) to a Blob. */
export const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const res = await fetch(dataUrl)
  return res.blob()
}
